<template>
    <div id="home">
        <div id="table-box">
            <div id="tab-wrapper">
                <div v-if="!isPaedActive" id="tabs">
                    <div
                        v-for="(tab, index) in tabs"
                        :key="tab.id"
                        class="tab-btn"
                        :class="tabCSSClass(tab)"
                        @click="selectTab(tab)"
                    >
                        <span>
                            <span class="tab-order">{{ index + 1 }}</span>
                            {{ tab.name }}
                        </span>
                    </div>
                </div>
                <div v-else id="paed-box">
                    <div id="paed-filter-box">
                        <div id="paed-filter">
                            <label for="paed-change-since">
                                Preisänderungsdienst (PÄD Termine):
                            </label>
                            <b-form-input
                                id="paed-change-since"
                                v-model.number="paedChangeSince"
                                type="number"
                                placeholder="PÄD Termine"
                                min="1"
                                @keypress="onlyAllowIntegers($event)"
                            />
                        </div>
                        <Info title="Preisänderungsdienst:">
                            Zeigt alle Artikel, die in den letzten x PÄD-Terminen (1. und 15.)
                            bepreist wurden.
                        </Info>
                    </div>
                </div>
                <div v-if="loading" id="loading">
                    <div id="loading-wrapper">
                        <span>Kartenreiter werden berechnet</span>
                        <font-awesome-icon id="loading-spinner" icon="spinner" spin />
                    </div>
                </div>
                <div v-else-if="errorMsg" id="error-container">
                    <span>
                        <font-awesome-icon icon="exclamation-triangle" class="icon" />
                        {{ errorMsg }}
                    </span>
                </div>
                <div v-else-if="tableData.length === 0" id="no-result-container">
                    Kein Artikel erfüllt die Kriterien des Kartenreiters oder der Filter.
                </div>
                <Table
                    v-else
                    :key="forceUpdateHelper"
                    :table-data="tableData"
                    :columns="tableColumns"
                />
                <TableFooter
                    :article-count="articleCount"
                    :article-count-with-price="tableData.filter(article => article.priceNew).length"
                    :selected-article-count="tableData.filter(article => !article.selected).length"
                />
            </div>
        </div>
        <InteractionBar :interactions="interactions">
            <template v-slot:main>
                <div class="prognose-rechnung bg-light">
                    <span class="toolbar-header font-weight-bold">Prognoserechnung (Tabelle)</span>
                    <span class="font-weight-bold">
                        <span :class="forecastCssClass">{{ forecastText }}</span>
                    </span>
                </div>
                <div class="prognose-rechnung bg-light">
                    <span class="toolbar-header">Prognoserechnung (gesamt)</span>
                    <span :class="totalForecastCssClass">{{ totalForecastText }}</span>
                </div>
            </template>
            <template v-slot:pricing>
                <div class="toolbar-card-wrapper">
                    <Autobepreisungoptions />
                    <Autobepreisung />
                    <DeletePrices />
                </div>
                <div class="toolbar-footer bg-dark">
                    <SmartButton id="set-new-price-btn" variant="success" @click="setNewPricing()">
                        Bepreisung durchführen
                    </SmartButton>
                </div>
            </template>
            <template v-slot:filter>
                <div class="toolbar-card-wrapper">
                    <PriceChangeFilter />
                    <TableFilter />
                    <Blacklist />
                </div>

                <div class="toolbar-footer bg-dark">
                    <SmartButton @click="resetFilter()">Filter zurücksetzen</SmartButton>
                </div>
            </template>
            <template v-slot:save>
                <div class="toolbar-card-wrapper">
                    <SaveLoadPrices :articles="selectedAllArticles" />
                    <Export :articles="selectedAllArticles" />
                </div>
            </template>
        </InteractionBar>
    </div>
</template>

<script>
import { EventBus } from "@/utils/EventBus.js";

import Autobepreisungoptions from "./pricing/AutobepreisungOptions.vue";
import Autobepreisung from "./pricing/Autobepreisung.vue";
import Export from "./settings/Export.vue";
import Blacklist from "./filter/Blacklist";
import PriceChangeFilter from "./filter/PriceChangeFilter";
import TableFilter from "./filter/TableFilter";

import Table from "../shared/widgets/Table";
import TableFooter from "./TableFooter";

import Info from "@/components/widgets/Info";

import ArticleTableColumns from "./config/ArticleTableColumns";
import SaveLoadPrices from "./settings/SaveLoadPrices";
import DeletePrices from "@/views/home/settings/DeletePrices";
import InteractionBar from "@/views/shared/widgets/InteractionBar";
import SmartButton from "@/views/shared/widgets/SmartButton";

export default {
    name: "Home",
    components: {
        SmartButton,
        InteractionBar,
        DeletePrices,
        SaveLoadPrices,
        Table,
        TableFooter,
        Autobepreisungoptions,
        Autobepreisung,
        TableFilter,
        PriceChangeFilter,
        Export,
        Blacklist,
        Info
    },
    data() {
        return {
            // DATA
            loading: false,
            errorMsg: undefined,

            //Toolbar
            interactions: { pricing: "Bepreisung", filter: "Filter", save: "Import/Export" },

            //Table
            basicTableColumns: ArticleTableColumns
        };
    },
    computed: {
        tableColumns() {
            // There is an extra column for Preisänderungsdienst
            if (this.$store.state.tabs.isPaedActive) {
                // slice is used to copy the array to safe add an element
                // and concat to return new Array
                return this.basicTableColumns.slice().concat({
                    key: "lastAbdaChange",
                    label: "Datum",
                    thStyle: { width: "96px" },
                    sortable: true
                });
            }
            return this.basicTableColumns;
        },
        articleCount() {
            return this.tableData.length;
        },
        tableData() {
            return this.$store.state.data.currentTabData;
        },
        selectedAllArticles() {
            return this.$store.state.data.allArticles.filter(article => !article.selected);
        },
        tabs() {
            return this.$store.state.tabs.tabs;
        },
        selectedTab() {
            return this.$store.state.tabs.selectedTab;
        },
        tabCSSClass() {
            return tab => {
                if (!(tab.hasOwnProperty("articleCount") && tab.articleCount > 0)) {
                    return "tab-btn-disabled";
                }
                if (tab === this.selectedTab) {
                    return "selected";
                }
            };
        },
        isPaedActive() {
            return this.$store.state.tabs.isPaedActive;
        },
        paedChangeSince: {
            get() {
                return this.$store.state.tabs.paedChangeSince;
            },
            set(value) {
                this.$store.commit("setPaedChangeSince", parseInt(value) || 0);
                this.$store.dispatch("calculateAndFilterTabData");
            }
        },
        forecastText() {
            if (this.$store.getters.forecast.toFixed(2).toString().length > 12) {
                return "Zahl zu groß";
            }
            return (
                parseFloat(this.$store.getters.forecast).toLocaleString(undefined, {
                    minimumFractionDigits: 2
                }) + " €"
            );
        },
        forecastCssClass() {
            return {
                "text-success": this.$store.getters.forecast > 0,
                "text-danger": this.$store.getters.forecast < 0
            };
        },
        totalForecastText() {
            if (this.$store.getters.totalForecast.toFixed(2).toString().length > 12) {
                return "Zahl zu groß";
            }
            return (
                parseFloat(this.$store.getters.totalForecast).toLocaleString(undefined, {
                    minimumFractionDigits: 2
                }) + " €"
            );
        },
        totalForecastCssClass() {
            return {
                "text-success": this.$store.getters.totalForecast > 0,
                "text-danger": this.$store.getters.totalForecast < 0
            };
        },
        filterState() {
            return this.$store.state.filter;
        }
    },
    watch: {
        filterState: {
            deep: true,
            handler: "calculateTabDataAfterFilterUpdate"
        }
    },
    created() {
        EventBus.$on("navigation-change", this.loadData);
        EventBus.$on("navigation-ready", this.initData);
        EventBus.$on("pricing-started", () => {
            this.toolBarBtndisabled = true;
            this.settoolbarMsg("Artikel werden bepreist...", "");
        });
        EventBus.$on("pricing-success", () => {
            console.log("pricing-started");
            this.settoolbarMsg("Bepreisung erfolgreich", "success");
            this.toolBarBtndisabled = false;
        });
        EventBus.$on("pricing-failed", error => {
            console.error(error);
            this.settoolbarMsg("Ein Fehler bei der Bepreisung ist aufgetreten", "danger", 0);
        });
        //EventBus.$on("update-table", this.forceUpdate);
    },
    beforeDestroy() {
        EventBus.$off("navigation-change");
        EventBus.$off("navigation-ready");
        EventBus.$off("pricing-started");
        EventBus.$off("pricing-success");
        EventBus.$off("pricing-failed");
        //EventBus.$off("update-table");
    },
    methods: {
        initData: function() {
            this.$store.dispatch("initWithServerData").then(() => {
                this.loadData();
            });
        },
        loadData: function() {
            this.loading = true;
            this.errorMsg = undefined;
            this.$store
                .dispatch("loadArticleData")
                .then(() => {
                    this.selectTab(
                        this.tabs.find(
                            tab => tab.hasOwnProperty("articleCount") && tab.articleCount > 0
                        )
                    );
                })
                .catch(error => {
                    // todo: Error msg when allArticles is empty: no data for selected month
                    this.errorMsg = "Für den ausgewählten Monat sind keine Artikeldaten vorhanden.";
                    console.log(error);
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        selectTab: function(tab) {
            if (!(tab.hasOwnProperty("articleCount") && tab.articleCount > 0)) {
                return;
            }
            this.tableData.forEach(article => (article.selected = false));
            console.log("loading started");
            this.$store.commit("setSelectedTab", tab);
            this.$store
                .dispatch("calculateAndFilterTabData")
                .catch(() => {
                    this.error = true;
                })
                .finally(() => {
                    EventBus.$emit("update-table");
                });
        },
        setNewPricing() {
            let promise = this.$store.dispatch("setPricing");
            EventBus.$emit("update-table");
            return promise;
        },

        calculateTabDataAfterFilterUpdate() {
            return this.$store.dispatch("calculateAndFilterTabData");
        },
        resetFilter() {
            return this.$store.commit("resetFilter");
        }
    }
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss" scoped>
#home {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}
#tab-wrapper {
    height: 100%;
    display: flex;
    flex-direction: column;
}
#tabs {
    display: flex;
    flex-wrap: wrap;
}

.tab-btn {
    margin: 6px;
    border-radius: 4px;
    padding: 4px 8px;
    background: $gray-lighter;
    border: 1px solid $gray-light;
    cursor: pointer;
    transition: all 200ms cubic-bezier(0.43, 0, 0.37, 0.94);
    position: relative;
    user-select: none;
}

.tab-btn:hover {
    box-shadow: 0px 0px 5px rgba(0, 0, 0, 0.2);
}

.tab-btn-disabled {
    background: $gray-lightest;
    border: 1px solid $gray-lighter;
    color: $gray-light;
}

.tab-btn-disabled:hover {
    box-shadow: 0px 0px 0px;
}

.tab-btn.selected {
    background: $lups;
    color: white;
}
.tab-order {
    background: white;
    border-radius: 2px;
    width: 20px;
    height: 20px;
    color: black;
    border: 1px solid rgba(0, 0, 0, 0.2);
    display: inline-flex;
    justify-content: center;
    align-items: center;
    padding: 2px;
}

#paed-box {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
}
#paed-box > * {
    margin: 5px;
}
#paed-filter-box {
    display: flex;
}

#table-box {
    background: white;
    box-shadow: 0 0 18px -2px $gray;
    flex: 1;
    overflow-y: auto;
}

#no-result-container,
#error-container,
#loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex: 1;
    font-size: 1.6em;
}
#loading-wrapper > * {
    margin: 8px;
}
#error-container .icon {
    color: $danger;
}
#table-component {
    /* 1080 is HD but the scrollbar on the side needs space as well :/ */
    min-width: 1070px;
}
#interaction-box {
    display: flex;
    flex-wrap: wrap;
}

#toolbar {
    display: flex;
    flex-wrap: wrap;
    width: 100%;
    box-shadow: 0px -2px 10px 0px rgba(0, 0, 0, 0.42);
    z-index: 1;
}

#toolbar-buttons {
    display: flex;
    flex: 2 1 auto;
    flex-wrap: wrap;
    justify-content: flex-end;
}

.prognose-rechnung,
.toolbar-interaction {
    display: flex;
    flex-direction: column;
    padding: 12px;
    justify-content: center;
}

.prognose-rechnung {
    flex: 1 0 260px;
    padding: 5px 12px;
    margin-right: 1px;
}

.toolbar-card-wrapper {
    width: 100vw;
    background: white;
    display: flex;
    flex-wrap: wrap;
    justify-content: space-evenly;
    padding: 8px;
    overflow-y: auto;
    max-height: 60vh;
}

.toolbar-card-wrapper > * {
    margin: 5px;
    flex: 1;
    min-width: 280px;
    max-width: 420px;
}

.toolbar-footer {
    display: flex;
    justify-content: space-between;
    padding: 12px;
    border-top: 1px solid $gray;
}
.toolbar-footer > button {
    width: 260px;
    margin-left: auto;
}
</style>
